// ajay:code 19-08-26
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Search } from "lucide-react";
import { productAPI, pfmeaAPI } from "../../services/api";

export default function ClientPfmeaTemplate() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [search, setSearch] = useState("");
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    productAPI.list().then((r) => setProducts(r.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: pageSize, offset: (page - 1) * pageSize };
    if (productId) params.product_id = productId;
    if (search.trim()) params.search = search.trim();
    pfmeaAPI
      .listDocuments(params)
      .then((r) => {
        setDocuments(r.data.documents || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [productId, search, page]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setPage(1);
  }, [productId, search]);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>PFMEA Reports</h1>
        <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
          Process Failure Mode & Effect Analysis
        </p>
      </div>

      <div
        className="card"
        style={{
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
        }}
      >
        <div
          className="form-group"
          style={{ minWidth: 240, flex: "1 1 240px" }}
        >
          <label>Search (Part ID / Name)</label>
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text3)",
              }}
            />
            <input
              type="text"
              placeholder="e.g. P001 or Part1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
        </div>
        <div className="form-group" style={{ minWidth: 200 }}>
          <label>Part</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">All Parts</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
        {(search || productId) && (
          <button
            className="btn-secondary"
            onClick={() => {
              setSearch("");
              setProductId("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>FMEA No.</th>
                <th>Part</th>
                <th>Rev No.</th>
                <th>FMEA Date</th>
                <th>Approved By</th>
                <th style={{ width: 70 }}>View</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No PFMEA documents yet
                  </td>
                </tr>
              )}
              {documents.map((d) => (
                <tr key={d.id}>
                  <td className="mono">{d.fmea_no || "—"}</td>
                  <td style={{ fontWeight: 500 }}>
                    {d.product_name}{" "}
                    <span className="text3 mono" style={{ fontSize: 11 }}>
                      ({d.product_code})
                    </span>
                  </td>
                  <td className="text2">{d.rev_no || "—"}</td>
                  <td>
                    {d.fmea_date
                      ? new Date(d.fmea_date).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="text2">{d.approved_by || "—"}</td>
                  <td>
                    <button
                      title="View"
                      onClick={() => navigate(`/client/pfmea/${d.id}`)}
                      style={iconBtn}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > pageSize && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
            {total}
          </span>
          <button
            className="btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <button
            className="btn-secondary"
            disabled={page * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
